import { groq } from "next-sanity"

import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { getProductProjectionAttributes } from "../../shared/utils/getArticleInformation"

/**
 * Retrieves articles from a given customer with their corresponding standalone price data
 *
 * @param {object} [args]
 * @param {string} [args.customerNumber] - the number of the customer
 * @param {string} [args.locale] - the locale
 * @returns {object<{items: array}>}
 */
export const queryCustomerArticleDiscounts = {
  async customerArticleDiscounts(_parent, args, context, _info) {
    const { customerNumber, locale = AppSettings.defaultLocale(), cdn = true, limit = 100, page = 0 } = args

    const config = AppSettings.sanityConfigByLang(locale)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const commerceApi = CommerceService.api()

    try {
      const customer = await getCustomer({ number: customerNumber })
      const associationNumber = customer?.custom?.fields?.associationNumber // Verbandnummer

      // only do this if customer and associationNumber was found
      if (!customer?.id || !associationNumber) return null

      // retrieves the channel ID by the association number from the given customer
      const channelId = (
        await commerceApi
          .channels()
          .get({ queryArgs: { where: `key="VERBAND-${associationNumber}"` } })
          .execute()
      )?.body?.results?.[0]?.id

      if (!channelId) return null

      // retrieves the standalone price data by channel ID
      const {
        results: standalonePriceDataByChannel,
        total,
        limit: discountLimit,
        offset: discountOffset,
      } = await getAllCustomerStandalonePrices(channelId, limit, page)

      if (!standalonePriceDataByChannel?.length) return null

      // returns an array of article SKUs
      const skuList = standalonePriceDataByChannel?.map(({ sku }) => sku)
      const articlesConfiguration = await getArticlesConfiguration(skuList)

      // retrieves article data from the given SKU list
      const articles = await getArticlesFromSkuArray(client, skuList)

      if (!articles?.length) return null

      // returns an array of objects with article data from the customer with their corresponding standalone price data
      let articleDiscountData = []

      standalonePriceDataByChannel.map(({ sku, tiers, value, validFrom, validUntil }) => {
        const filteredArticles = articles?.filter((article) => article?.sku === sku)
        const articleConfig = articlesConfiguration?.find(
          (articleConfig) => articleConfig?.key === `orphan-article-${sku}`,
        )

        const { sellableInDecimals } = getProductProjectionAttributes(articleConfig, sku)

        const defaultStandalonePrice = {
          sku: sku || "",
          title: filteredArticles?.[0]?.title || "",
          altTitle: filteredArticles?.[0]?.altTitle || "",
          amount: filteredArticles?.[0]?.amount,
          contents: filteredArticles?.[0]?.contents || "",
          container: filteredArticles?.[0]?.container || "",
          quantity: 1,
          price: value,
          sellableInDecimals: sellableInDecimals,
          validFrom: validFrom,
          validUntil: validUntil,
        }

        articleDiscountData.push(defaultStandalonePrice)

        // price tier data
        return tiers?.map(({ minimumQuantity, value }) =>
          articleDiscountData.push({
            sku: sku || "",
            title: filteredArticles?.[0]?.title || "",
            altTitle: filteredArticles?.[0]?.altTitle || "",
            amount: filteredArticles?.[0]?.amount,
            contents: filteredArticles?.[0]?.contents || "",
            container: filteredArticles?.[0]?.container || "",
            quantity: minimumQuantity || 1,
            price: value,
            sellableInDecimals: sellableInDecimals,
            validFrom: validFrom,
            validUntil: validUntil,
          }),
        )
      })

      if (!articleDiscountData?.length) return null

      return {
        items: articleDiscountData,
        total: total,
        limit: discountLimit,
        offset: discountOffset,
      }
    } catch (err) {
      console.error(err)
      return null
    }
  },
}

/**
 * Retrieves article data from the provided SKU array
 *
 * @param {Object} client - the client object used for fetching data
 * @param {Array} skus - an array of SKUs to filter the articles
 * @returns {Promise<Array>}
 */
const getArticlesFromSkuArray = async (client, skus) => {
  const draftsFilter = `&& !(_id in path("drafts.**"))`
  const articles = await client.fetch(
    groq`*[_type=="article" && sku in $skus ${draftsFilter}] {
      "sku": sku,
      "title": title,
      "altTitle": altTitle,
      "amount": amount,
      "contents": contents,
      "container": container,
    }`,
    { skus },
  )
  return articles
}

/**
 * Retrieves all the results without a limit of commercetools
 * Maximum CT limit is 500
 *
 * @param {String} channelId
 *
 * @returns {Promise<Object>}
 * @throws {Error}
 */
const getAllCustomerStandalonePrices = async (channelId, pageSize, page) => {
  const commerceApi = CommerceService.api()

  if (pageSize != null && page != null) {
    const response = (
      await commerceApi
        .standalonePrices()
        .get({
          queryArgs: {
            where: `channel(id="${channelId}")`,
            limit: pageSize,
            offset: pageSize * page,
          },
        })
        .execute()
    )?.body

    return {
      results: response.results,
      limit: response.limit,
      offset: response.offset,
      total: response.total,
    }
  }
  let results = []
  let offset = 0
  let limit = 500
  let total = 0

  do {
    const response = (
      await commerceApi
        .standalonePrices()
        .get({
          queryArgs: {
            where: `channel(id="${channelId}")`,
            limit: limit,
            offset: offset,
          },
        })
        .execute()
    )?.body

    results = results.concat(response.results)
    total = response.total
    offset += limit
  } while (results.length < total)

  return { results: results, limit: limit, offset: 0, total: total }
}

/**
 * Retrieves articles from the commercetools
 *
 * @param {Array} skuList
 * @returns {Promise<Array>}
 * @throws {Error}
 */
const getArticlesConfiguration = async (skuList) => {
  if (!skuList || skuList.length === 0) throw new Error("No sku list provided")

  let results = []
  let offset = 0
  let limit = 500
  let total = 0
  let currentLastIndex = limit

  const commerceApi = CommerceService.api()

  do {
    const skus = []
    const keys = []

    for (let index = offset; index < currentLastIndex; index++) {
      keys.push(`"orphan-article-${skuList[index]}"`)
      skus.push(`"${skuList[index]}"`)
    }

    const response = (
      await commerceApi
        .productProjections()
        .get({
          queryArgs: {
            where: `key in (${keys.join(",")}) and (masterVariant(sku in (${skus.join(
              ",",
            )})) or variants(sku in (${skus.join(",")})))`,
            limit: limit,
            offset: offset,
          },
        })
        .execute()
    )?.body

    if (!response || response.results?.length === 0) return results

    results = results.concat(response.results)
    total = response.total
    offset += limit
    currentLastIndex = limit + currentLastIndex < total ? currentLastIndex + limit : total
  } while (results.length < total)

  return results
}
